<template>
  <div class="param-container">
    <el-card>
      <div slot="header" class="card-header">
        <span>系统参数设置</span>
        <el-button type="primary" size="small" @click="handleRefresh">
          <i class="el-icon-refresh"></i> 刷新缓存
        </el-button>
      </div>
      
      <el-tabs v-model="activeCategory" @tab-click="handleCategoryChange">
        <el-tab-pane label="WebSocket配置" name="websocket"></el-tab-pane>
        <el-tab-pane label="会话配置" name="conversation"></el-tab-pane>
        <el-tab-pane label="消息配置" name="message"></el-tab-pane>
        <el-tab-pane label="安全配置" name="security"></el-tab-pane>
        <el-tab-pane label="API配置" name="api"></el-tab-pane>
      </el-tabs>
      
      <el-table :data="filteredParams" style="width: 100%" v-loading="loading">
        <el-table-column prop="paramName" label="参数名称" width="200"></el-table-column>
        <el-table-column label="当前值" width="250">
          <template slot-scope="scope">
            <div v-if="!scope.row.editing">
              <span style="font-weight: bold; color: #409EFF;">{{ scope.row.paramValue }}</span>
              <span v-if="scope.row.unit" style="color: #999; margin-left: 5px;">({{ scope.row.unit }})</span>
            </div>
            <div v-else>
              <el-input 
                v-model="scope.row.newValue" 
                :type="scope.row.paramType === 'int' || scope.row.paramType === 'long' ? 'number' : 'text'"
                size="small"
                style="width: 150px;"
              ></el-input>
              <span v-if="scope.row.unit" style="color: #999; margin-left: 5px;">{{ scope.row.unit }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="推荐值" width="150">
          <template slot-scope="scope">
            <span style="color: #67C23A;">{{ scope.row.recommendValue }}</span>
          </template>
        </el-table-column>
        <el-table-column label="默认值" width="150">
          <template slot-scope="scope">
            <span style="color: #909399;">{{ scope.row.defaultValue }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="说明" show-overflow-tooltip></el-table-column>
        <el-table-column label="操作" width="180">
          <template slot-scope="scope">
            <div v-if="scope.row.isEditable">
              <el-button 
                v-if="!scope.row.editing" 
                type="text" 
                size="small" 
                @click="handleEdit(scope.row)"
              >
                编辑
              </el-button>
              <div v-else>
                <el-button type="text" size="small" @click="handleSave(scope.row)">保存</el-button>
                <el-button type="text" size="small" @click="handleCancel(scope.row)">取消</el-button>
              </div>
            </div>
            <span v-else style="color: #999;">不可编辑</span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    
    <el-card style="margin-top: 20px;">
      <div slot="header">
        <span>使用说明</span>
      </div>
      <div class="tips">
        <p><strong>推荐值</strong>：生产环境建议使用推荐值，已经过充分测试。</p>
        <p><strong>当前值</strong>：开发环境可以设置较小的值便于测试，但上线前务必调整为推荐值。</p>
        <p><strong>热更新</strong>：修改参数后会立即生效，无需重启服务。</p>
        <el-divider></el-divider>
        <p><strong>参数说明：</strong></p>
        <ul>
          <li><strong>同步在线状态间隔</strong>：定时同步WebSocket在线状态到数据库的间隔，推荐30秒</li>
          <li><strong>检测用户/客服状态间隔</strong>：检测WebSocket断开的间隔，推荐30秒</li>
          <li><strong>离线判定阈值</strong>：连续多少次检测到离线才标记为离线，防止网络抖动误判</li>
          <li><strong>会话分配间隔</strong>：自动分配待处理会话的间隔</li>
          <li><strong>会话转接间隔</strong>：检测客服离线并转接会话的间隔</li>
        </ul>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getParamsByCategory, updateParamValue, refreshCache } from '@/api/param'

export default {
  name: 'SystemParam',
  data() {
    return {
      loading: false,
      activeCategory: 'websocket',
      params: [],
      filteredParams: []
    }
  },
  created() {
    this.loadParams()
  },
  methods: {
    async loadParams() {
      this.loading = true
      try {
        const response = await getParamsByCategory(this.activeCategory)
        this.params = response.data || []
        this.filteredParams = this.params.map(p => ({
          ...p,
          editing: false,
          newValue: p.paramValue
        }))
      } catch (error) {
        this.$message.error('加载参数失败')
      } finally {
        this.loading = false
      }
    },
    handleCategoryChange() {
      this.loadParams()
    },
    handleEdit(row) {
      row.editing = true
      row.newValue = row.paramValue
    },
    handleCancel(row) {
      row.editing = false
      row.newValue = row.paramValue
    },
    async handleSave(row) {
      if (row.newValue === row.paramValue) {
        row.editing = false
        return
      }
      
      try {
        await updateParamValue(row.paramKey, row.newValue)
        this.$message.success('参数更新成功，已热更新到内存')
        row.paramValue = row.newValue
        row.editing = false
      } catch (error) {
        this.$message.error('更新失败: ' + (error.message || '未知错误'))
      }
    },
    async handleRefresh() {
      try {
        await refreshCache()
        this.$message.success('缓存刷新成功')
        this.loadParams()
      } catch (error) {
        this.$message.error('刷新失败')
      }
    }
  }
}
</script>

<style scoped>
.param-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tips {
  line-height: 1.8;
  color: #666;
}

.tips ul {
  padding-left: 20px;
}

.tips li {
  margin-bottom: 8px;
}
</style>
